import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import {
  PhxStringCellPrefixIcon,
  PhxTableCellStringInput,
  PhxTableCellStringSegment,
  PhxTableIconStyle,
} from '../../phx-table.types';
import { Bullet16 } from '../../../icon/icon-map';
import { DEFAULT_PRIMARY_CONFIG } from '../phx-table-cell.const';
import { normalizeCellStringInput, trackBySegmentValue } from '../phx-table-cell.utils';

@Component({
  selector: 'phx-table-cell-string',
  templateUrl: './phx-table-cell-string.component.html',
  styleUrls: ['./phx-table-cell-string.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-table-cell-string',
  },
})
export class PhxTableCellStringComponent implements OnChanges {
  /** Primary line text (medium / standard). Accepts a plain string or a styled segment. */
  @Input() phxPrimary: string | PhxTableCellStringSegment | undefined;
  /** Secondary subtext (small / subdued). */
  @Input() phxSecondary: PhxTableCellStringInput | undefined;
  /** Tertiary subtext (small / subdued). Same input shapes as `phxSecondary`. */
  @Input() phxTertiary: PhxTableCellStringInput | undefined;
  /** Icon(s) rendered before the primary line. */
  @Input() phxPrefixIcon: PhxStringCellPrefixIcon | undefined;
  /** Icon rendered inline after the primary text. */
  @Input() phxInlineIcon: string | PhxTableIconStyle | undefined;
  /** Render the primary line as an in-app link (`routerLink`). */
  @Input() phxPrimaryLink: string | unknown[] | undefined;
  /** Target for the primary link anchor (e.g. `_blank`). */
  @Input() phxPrimaryLinkTarget: string | undefined;

  public readonly Bullet16Icon = Bullet16;
  public readonly trackBySegment = trackBySegmentValue;
  public primaryValue: PhxTableCellStringSegment = { value: '', ...DEFAULT_PRIMARY_CONFIG };
  public secondaryValue: PhxTableCellStringSegment[] | undefined;
  public tertiaryValue: PhxTableCellStringSegment[] | undefined;
  public prefixIcon: [PhxTableIconStyle] | [PhxTableIconStyle, PhxTableIconStyle] | undefined;
  public inlineIcon: PhxTableIconStyle | undefined;

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxPrimary, phxSecondary, phxTertiary, phxPrefixIcon, phxInlineIcon } = changes;

    if (phxPrimary) {
      this.primaryValue = this.getPrimaryValue();
    }

    if (phxSecondary) {
      this.secondaryValue = this.phxSecondary ? normalizeCellStringInput(this.phxSecondary) : undefined;
    }

    if (phxTertiary) {
      this.tertiaryValue = this.phxTertiary ? normalizeCellStringInput(this.phxTertiary) : undefined;
    }

    if (phxPrefixIcon) {
      this.prefixIcon = this.getPrefixIconValue();
    }

    if (phxInlineIcon) {
      this.inlineIcon = this.getInlineIconValue();
    }
  }

  public trackByPrefixIconFn(_: number, icon: PhxTableIconStyle): string {
    return icon.icon;
  }

  private getPrimaryValue(): PhxTableCellStringSegment {
    if (!this.phxPrimary) return { value: '', ...DEFAULT_PRIMARY_CONFIG };

    if (typeof this.phxPrimary === 'string') {
      return { ...DEFAULT_PRIMARY_CONFIG, value: this.phxPrimary };
    } else {
      return { ...DEFAULT_PRIMARY_CONFIG, ...this.phxPrimary };
    }
  }

  private getPrefixIconValue(): [PhxTableIconStyle] | [PhxTableIconStyle, PhxTableIconStyle] | undefined {
    if (!this.phxPrefixIcon) return undefined;

    if (typeof this.phxPrefixIcon === 'string') {
      return [{ icon: this.phxPrefixIcon }];
    }

    if (Array.isArray(this.phxPrefixIcon)) {
      const [first, second] = this.phxPrefixIcon;
      return [
        typeof first === 'string' ? { icon: first } : first,
        typeof second === 'string' ? { icon: second } : second,
      ];
    }

    return [this.phxPrefixIcon];
  }

  private getInlineIconValue(): PhxTableIconStyle | undefined {
    if (!this.phxInlineIcon) {
      return undefined;
    }

    if (typeof this.phxInlineIcon === 'string') {
      return { icon: this.phxInlineIcon };
    }

    return this.phxInlineIcon;
  }
}
